import type { AutomationResult } from '../types'

/**
 * IAutomationProvider — contract for portal automation providers.
 * No business rules; each supplier may implement its own provider later.
 */
export interface IAutomationProvider {
  readonly supplierCode: string

  initialize(): Promise<void>
  authenticate(credentials?: Record<string, string>): Promise<boolean>
  open(url?: string): Promise<void>
  navigate(path: string): Promise<void>
  search(term: string): Promise<unknown[]>
  extract(selector?: string): Promise<unknown[]>
  download(resourceId: string): Promise<Blob | null>
  close(): Promise<void>
  healthCheck(): Promise<{ ok: boolean; latency: number }>
}

/**
 * BaseAutomationProvider — shared stubs (no real browser / scraping).
 */
export abstract class BaseAutomationProvider implements IAutomationProvider {
  abstract readonly supplierCode: string
  protected ready = false
  protected openState = false

  async initialize(): Promise<void> {
    await delay(5)
    this.ready = true
  }

  async authenticate(_credentials?: Record<string, string>): Promise<boolean> {
    await this.ensureReady()
    await delay(5)
    // Infrastructure only — no real login
    return true
  }

  async open(_url?: string): Promise<void> {
    await this.ensureReady()
    this.openState = true
  }

  async navigate(_path: string): Promise<void> {
    await this.ensureOpen()
    await delay(5)
  }

  async search(term: string): Promise<unknown[]> {
    await this.ensureOpen()
    await delay(10)
    return [{ id: `${this.supplierCode}-mock`, term, source: 'automation-stub' }]
  }

  async extract(_selector?: string): Promise<unknown[]> {
    await this.ensureOpen()
    return []
  }

  async download(_resourceId: string): Promise<Blob | null> {
    await this.ensureOpen()
    return null
  }

  async close(): Promise<void> {
    this.openState = false
  }

  async healthCheck(): Promise<{ ok: boolean; latency: number }> {
    await this.ensureReady()
    const started = performance.now()
    await delay(5)
    return { ok: this.ready, latency: Math.round(performance.now() - started) }
  }

  protected async ensureReady(): Promise<void> {
    if (!this.ready) await this.initialize()
  }

  protected async ensureOpen(): Promise<void> {
    await this.ensureReady()
    if (!this.openState) await this.open()
  }
}

/** Optional helper for providers that want timeout wrapping. */
export async function runWithTimeout<T>(fn: () => Promise<T>, timeoutMs: number): Promise<T> {
  return Promise.race([
    fn(),
    new Promise<T>((_, reject) => {
      setTimeout(() => reject(new Error(`Timeout após ${timeoutMs}ms`)), timeoutMs)
    }),
  ])
}

export function toFailedResult(error: unknown, executionTime = 0): AutomationResult {
  return {
    success: false,
    executionTime,
    itemsFound: 0,
    warnings: [],
    errors: [error instanceof Error ? error.message : 'Erro de automação'],
    logs: [],
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
